import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { api, eur, dfr, factures as facturesApi } from './pilotageApi';

const TABS = [
  { k: 'devis', l: 'Le devis' },
  { k: 'argent', l: "L'argent" },
  { k: 'terrain', l: 'Le terrain' },
  { k: 'histo', l: "L'historique" },
];

export default function FicheDossier() {
  const { id } = useParams();
  const nav = useNavigate();
  const [etat, setEtat] = useState(null);
  const [quote, setQuote] = useState(null);
  const [histo, setHisto] = useState([]);
  const [tab, setTab] = useState('devis');
  const [flash, setFlash] = useState(null);
  const [busy, setBusy] = useState(false);
  const [heureRetour, setHeureRetour] = useState('');
  const [products, setProducts] = useState([]);
  const [err, setErr] = useState(null);

  const load = useCallback(() => {
    Promise.all([api.etat(id), api.devis(id)])
      .then(([e, q]) => { setEtat(e); setQuote(q); })
      .catch((ex) => { if (ex.code === 401 || ex.code === 403) nav('/connexion'); else setErr(ex.message); });
    api.historique(id).then((h) => setHisto(h.historique || h.events || h || [])).catch(() => {});
    api.products().then((p) => setProducts(Array.isArray(p) ? p : (p.products || p.items || []))).catch(() => {});
  }, [id, nav]);
  useEffect(() => { load(); }, [load]);

  function say(msg) { setFlash(msg); setTimeout(() => setFlash(null), 3000); }

  async function run(fn, msg) {
    setBusy(true);
    try { await fn(); say(msg); load(); }
    catch (e) { say(`⚠️ ${e.detail || e.message}`); }
    finally { setBusy(false); }
  }

  async function saveDevis(patch) {
    setBusy(true);
    try {
      const r = await api.patchDevis(id, patch);
      setQuote(r.dossier); setEtat(r.synthese); say('Devis mis à jour.');
    } catch (e) { say(`⚠️ ${e.detail || e.message}`); }
    finally { setBusy(false); }
  }

  // Action principale : route l'événement vers le bon appel serveur.
  async function doAction(ev) {
    if (!ev) return;
    if (ev === 'payer_acompte') return run(() => api.paiementManuel(id, 'acompte'), 'Acompte encaissé.');
    if (ev === 'signer_contrat') return run(() => api.transition(id, 'signer_contrat', { nom: quote?.client_nom }), 'Contrat signé.');
    if (ev === 'retourner') { setTab('terrain'); say('Saisissez l\'heure de retour dans « Le terrain ».'); return; }
    return run(() => api.transition(id, ev), 'Dossier mis à jour.');
  }

  if (err) return <div className="wrap"><div className="nt ba">{err}</div></div>;
  if (!etat || !quote) return <div className="load" data-testid="fiche-loading">Chargement du dossier…</div>;

  const m = etat.montants;
  const acompteRegle = m.paye + 0.01 >= Number(quote.acompte || 0) && m.paye > 0;
  const ap = etat.action_principale;
  const verrous = etat.verrous;

  // Bouton qui lève un verrou (à côté du bouton désactivé), quand c'est possible.
  const unblock = (() => {
    if (ap.autorise) return null;
    if (!verrous.preparation.autorise) return { l: "Encaisser l'acompte", fn: () => api.paiementManuel(id, 'acompte'), msg: 'Acompte encaissé.' };
    if (!verrous.cloture.autorise) return { l: 'Encaisser le solde', fn: () => api.paiementManuel(id, 'solde'), msg: 'Solde encaissé.' };
    return null;
  })();

  return (
    <>
      <div className="top">
        <button className="btn btn-s" data-testid="btn-retour-liste" onClick={() => nav('/pilotage/dossiers')}>← Dossiers</button>
        <div className="sp" />
        {flash && <span className="ctx" data-testid="flash">{flash}</span>}
        {!ap.autorise && ap.raison && <span className="lk" data-testid="action-lock">🔒 {ap.raison}</span>}
        {unblock && (
          <button className="btn btn-s btn-g" data-testid="btn-unblock" disabled={busy}
            onClick={() => run(unblock.fn, unblock.msg)}>{unblock.l}</button>
        )}
        {/* UN SEUL bouton d'action principale, toujours présent, libellé selon l'étape (§4.3) */}
        {ap.libelle && (
          <button className="btn btn-g" data-testid="btn-action-principale"
            disabled={busy || (ap.event ? !ap.autorise : etat.etat !== 'devis_envoye')}
            title={ap.raison || ''}
            onClick={() => (ap.event ? doAction(ap.event) : run(() => api.relance(id), 'Relance envoyée au client.'))}>
            {ap.libelle}
          </button>
        )}
      </div>

      {/* Barre dossier */}
      <div className="db" data-testid="barre-dossier">
        <div className="num" data-testid="dossier-numero">{quote.numero}</div>
        <div className="sep" />
        <div className="cli">{quote.client_nom}</div>
        <div className="sep" />
        <div className="kv">Profil<b style={{ textTransform: 'capitalize' }}>{quote.client_profile}</b></div>
        <div className="kv">Événement<b>{(quote.session?.date_livraison || quote.session?.date_evenement || '—').slice(0, 10)}</b></div>
        <div className="kv">Total<b data-testid="dossier-total">{eur(m.total)}</b></div>
        <div className="kv">Acompte{acompteRegle ? <b style={{ color: 'var(--gr)' }}>réglé</b> : <b style={{ color: 'var(--ru)' }}>{eur(quote.acompte)}</b>}</div>
        <div className="kv">Reste dû<b data-testid="dossier-reste" style={{ color: m.reste_du > 0.01 ? 'var(--ru)' : 'var(--gr)' }}>{eur(m.reste_du)}</b></div>
      </div>

      {/* Bandeau apporteur (Lot 6) */}
      {quote.partenaire_id && (
        <div data-testid="bandeau-partenaire" style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 20px', background: 'var(--ams)', borderBottom: '1px solid var(--line)', color: 'var(--am)', fontSize: 13, fontWeight: 600 }}>
          <span>🤝 Apporté par <b>{quote.partenaire_nom}</b>{quote.partenaire_montage === 'pour_lui' ? ' (pour lui-même)' : ' (pour son client)'} — commission <b>{eur(Number(m.total) * Number(quote.commission_taux || 15) / 100)}</b>{(quote.commission_verse || {}).verse ? ' · versée' : ''}</span>
        </div>
      )}

      {/* Frise */}
      <div className="fr" data-testid="frise">
        {etat.frise.map((s) => (
          <div key={s.etat} className={`st ${s.statut === 'fait' ? 'dn' : s.statut === 'courant' ? 'nw' : ''}`} data-testid={`frise-${s.etat}`}>
            <span className="pt" /><div className="lb">{s.libelle}</div>
          </div>
        ))}
      </div>

      {/* Onglets */}
      <div className="tb" data-testid="onglets">
        {TABS.map((t) => (
          <button key={t.k} className={`tab ${tab === t.k ? 'on' : ''}`} data-testid={`tab-${t.k}`} onClick={() => setTab(t.k)}>{t.l}</button>
        ))}
      </div>

      <div className="wrap">
        {tab === 'devis' && <TabDevis quote={quote} m={m} etat={etat.etat} products={products} busy={busy} onSave={saveDevis} />}
        {tab === 'argent' && (
          <TabArgent quote={quote} m={m} acompteRegle={acompteRegle} busy={busy}
            onEncaisser={(type) => run(() => api.paiementManuel(id, type), type === 'acompte' ? 'Acompte encaissé.' : 'Solde encaissé.')} />
        )}
        {tab === 'terrain' && (
          <TabTerrain etat={etat} heureRetour={heureRetour} setHeureRetour={setHeureRetour} busy={busy}
            onRetour={() => run(() => api.transition(id, 'retourner', { heure_retour: heureRetour }), 'Retour enregistré.')} />
        )}
        {tab === 'histo' && <TabHisto quote={quote} histo={histo} />}
      </div>
    </>
  );
}

function TabDevis({ quote, m, etat, products, onSave, busy }) {
  const items = quote.items || [];
  const editable = etat !== 'clos';
  const acompteEmis = m.paye > 0.01; // facture d'acompte émise → acompte figé
  const [editing, setEditing] = React.useState(false);
  const [addPid, setAddPid] = React.useState('');
  const [libre, setLibre] = React.useState({ nom: '', prix: '' });
  const [dates, setDates] = React.useState({
    date_livraison: quote.session?.date_livraison || '',
    date_reprise: quote.session?.date_reprise || '',
    heure_reprise: quote.session?.heure_reprise || '10:00',
    adresse_livraison: quote.adresse_livraison || quote.session?.adresse || '',
  });
  React.useEffect(() => {
    setDates({
      date_livraison: quote.session?.date_livraison || '',
      date_reprise: quote.session?.date_reprise || '',
      heure_reprise: quote.session?.heure_reprise || '10:00',
      adresse_livraison: quote.adresse_livraison || quote.session?.adresse || '',
    });
  }, [quote]);

  // Lignes éditables = tout sauf la livraison (réinsérée par le serveur).
  const editables = items.filter((it) => it.product_type !== 'livraison');
  const livraison = items.find((it) => it.product_type === 'livraison');

  const asPayloadItems = (list) => list.map((it) => (
    it.product_id ? { product_id: it.product_id, quantite: it.quantite }
      : { nom: it.product_nom, quantite: it.quantite, prix_unitaire: it.prix_unitaire, type: 'libre' }
  ));

  const save = (list, extra = {}) => onSave({ items: asPayloadItems(list), ...dates, ...extra });

  const setQty = (i, q) => {
    if (q < 1) return;
    const list = editables.map((it, j) => (j === i ? { ...it, quantite: q } : it));
    save(list);
  };
  const remove = (i) => save(editables.filter((_, j) => j !== i));
  const addArticle = () => {
    const p = products.find((x) => x.id === addPid);
    if (!p) return;
    save([...editables, { product_id: p.id, product_nom: p.nom, quantite: 1 }]);
    setAddPid('');
  };
  const addLibre = () => {
    const prix = parseFloat(libre.prix);
    if (!libre.nom.trim() || !(prix > 0)) return;
    save([...editables, { product_nom: libre.nom.trim(), prix_unitaire: prix, quantite: 1 }]);
    setLibre({ nom: '', prix: '' });
  };
  const saveDates = () => save(editables);

  return (
    <div className="bk" data-testid="tab-devis-content">
      <div className="bh">
        <h2>Le devis</h2><div className="sp" />
        {!editable && <span className="bg bg-n" data-testid="devis-verrouille">🔒 Verrouillé (dossier clos)</span>}
        {editable && !editing && <button className="btn btn-s btn-p" data-testid="btn-editer-devis" onClick={() => setEditing(true)}>Modifier le devis</button>}
        {editable && editing && <button className="btn btn-s" data-testid="btn-fin-edition" onClick={() => setEditing(false)}>Terminer</button>}
      </div>

      <div style={{ padding: 12 }}>
        {acompteEmis && editing && <div className="nt wa" data-testid="note-acompte-fige">Acompte déjà encaissé : les modifications ajustent le <b>solde</b>, l'acompte facturé reste inchangé.</div>}

        <table>
          <thead><tr><th>Article</th><th style={{ textAlign: 'right' }}>Qté</th><th style={{ textAlign: 'right' }}>P.U.</th><th style={{ textAlign: 'right' }}>Total</th>{editing && <th />}</tr></thead>
          <tbody>
            {editables.map((it, i) => (
              <tr key={i} data-testid={`ligne-${i}`}>
                <td><b>{it.product_nom}</b>{it.economie > 0 && <span className="bg bg-g" style={{ marginLeft: 8 }}>−{Math.round((it.taux_remise || 0) * 100)}%</span>}{!it.product_id && <span className="bg bg-n" style={{ marginLeft: 6 }}>libre</span>}</td>
                <td className="nu">
                  {editing ? (
                    <span style={{ display: 'inline-flex', gap: 4, alignItems: 'center' }}>
                      <button className="btn btn-s" data-testid={`qty-moins-${i}`} disabled={busy || it.quantite <= 1} onClick={() => setQty(i, it.quantite - 1)}>−</button>
                      <b data-testid={`qty-${i}`} style={{ minWidth: 18, display: 'inline-block' }}>{it.quantite}</b>
                      <button className="btn btn-s" data-testid={`qty-plus-${i}`} disabled={busy} onClick={() => setQty(i, it.quantite + 1)}>+</button>
                    </span>
                  ) : it.quantite}
                </td>
                <td className="nu">{eur(it.prix_unitaire)}</td>
                <td className="nu"><b>{eur(it.sous_total)}</b></td>
                {editing && <td className="nu"><button className="btn btn-s btn-r" data-testid={`remove-${i}`} disabled={busy} onClick={() => remove(i)}>✕</button></td>}
              </tr>
            ))}
            {livraison && (
              <tr data-testid="ligne-livraison">
                <td><b>{livraison.product_nom}</b> <span className="bg bg-b" style={{ marginLeft: 6 }}>auto</span></td>
                <td className="nu">1</td><td className="nu">{eur(livraison.prix_unitaire)}</td><td className="nu"><b>{eur(livraison.sous_total)}</b></td>{editing && <td />}
              </tr>
            )}
          </tbody>
        </table>

        {editing && (
          <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginTop: 12, alignItems: 'flex-end' }}>
            <div className="f" style={{ minWidth: 220 }}>
              <label>Ajouter un article du catalogue</label>
              <div style={{ display: 'flex', gap: 6 }}>
                <select value={addPid} data-testid="select-article" onChange={(e) => setAddPid(e.target.value)}>
                  <option value="">— choisir —</option>
                  {products.map((p) => <option key={p.id} value={p.id}>{p.nom} ({eur(p.prix_public)})</option>)}
                </select>
                <button className="btn btn-s btn-g" data-testid="btn-add-article" disabled={busy || !addPid} onClick={addArticle}>+ Article</button>
              </div>
            </div>
            <div className="f" style={{ minWidth: 240 }}>
              <label>Ligne libre (consommable, prestation…)</label>
              <div style={{ display: 'flex', gap: 6 }}>
                <input placeholder="Désignation" data-testid="input-libre-nom" value={libre.nom} onChange={(e) => setLibre({ ...libre, nom: e.target.value })} />
                <input type="number" placeholder="€" style={{ width: 80 }} data-testid="input-libre-prix" value={libre.prix} onChange={(e) => setLibre({ ...libre, prix: e.target.value })} />
                <button className="btn btn-s btn-g" data-testid="btn-add-libre" disabled={busy} onClick={addLibre}>+ Ligne</button>
              </div>
            </div>
          </div>
        )}

        {editing && (
          <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginTop: 12 }}>
            <div className="f"><label>Livraison</label><input type="date" data-testid="edit-date-livraison" value={dates.date_livraison} onChange={(e) => setDates({ ...dates, date_livraison: e.target.value })} onBlur={saveDates} /></div>
            <div className="f"><label>Reprise</label><input type="date" data-testid="edit-date-reprise" value={dates.date_reprise} onChange={(e) => setDates({ ...dates, date_reprise: e.target.value })} onBlur={saveDates} /></div>
            <div className="f"><label>Heure de reprise</label><input type="time" data-testid="edit-heure-reprise" value={dates.heure_reprise} onChange={(e) => setDates({ ...dates, heure_reprise: e.target.value })} onBlur={saveDates} /></div>
            <div className="f" style={{ minWidth: 220, flex: 1 }}><label>Adresse de livraison (recalcule la livraison)</label><input data-testid="edit-adresse" value={dates.adresse_livraison} onChange={(e) => setDates({ ...dates, adresse_livraison: e.target.value })} onBlur={saveDates} placeholder="Ville + code postal" /></div>
          </div>
        )}

        <div className="tt">
          {quote.economie_totale > 0 && <div className="tl"><span>Sous-total (prix public)</span><span className="v">{eur(quote.sous_total_public)}</span></div>}
          {quote.economie_totale > 0 && <div className="tl ds"><span>Remise appliquée</span><span className="v">− {eur(quote.economie_totale)}</span></div>}
          <div className="tl bg2"><span>Total TTC</span><span className="v" data-testid="devis-total">{eur(m.total)}</span></div>
          <div className="tl"><span>Acompte 30 %</span><span className="v" data-testid="devis-acompte">{eur(quote.acompte)}</span></div>
          <div className="tl"><span>Solde</span><span className="v" data-testid="devis-solde">{eur(quote.solde)}</span></div>
        </div>
      </div>
    </div>
  );
}

function TabArgent({ quote, m, acompteRegle, onEncaisser, busy }) {
  const [detail, setDetail] = React.useState(null);
  React.useEffect(() => {
    api.facturesDossier(quote.id).then(setDetail).catch(() => setDetail({ factures: [], transactions: [] }));
  }, [quote.id]);
  const ETAT_TX = {
    reussi: { l: 'Réussi', c: 'bg-g' }, en_attente: { l: 'En attente', c: 'bg-a' },
    echoue: { l: 'Échoué', c: 'bg-r' }, rembourse: { l: 'Remboursé', c: 'bg-n' },
  };
  const STATUT_FAC = { reglee: 'bg-g', partielle: 'bg-a', en_attente: 'bg-b', en_retard: 'bg-r', annulee: 'bg-n', avoir: 'bg-n' };
  return (
    <div className="bk" data-testid="tab-argent-content">
      <div className="bh"><h2>L'argent</h2></div>
      <div style={{ padding: 16 }}>
        <div className="tt" style={{ marginTop: 0 }}>
          <div className="tl"><span>Total dû</span><span className="v">{eur(m.total)}</span></div>
          <div className="tl"><span>Déjà payé</span><span className="v" style={{ color: 'var(--gr)' }}>{eur(m.paye)}</span></div>
          <div className="tl bg2"><span>Reste dû</span><span className="v" style={{ color: m.reste_du > 0.01 ? 'var(--ru)' : 'var(--gr)' }} data-testid="argent-reste">{eur(m.reste_du)}</span></div>
        </div>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 14 }}>
          {!acompteRegle && <button className="btn btn-g" data-testid="btn-encaisser-acompte" disabled={busy} onClick={() => onEncaisser('acompte')}>Encaisser l'acompte ({eur(quote.acompte)})</button>}
          {m.reste_du > 0.01 && acompteRegle && <button className="btn btn-g" data-testid="btn-encaisser-solde" disabled={busy} onClick={() => onEncaisser('solde')}>Encaisser le solde ({eur(m.reste_du)})</button>}
          {m.reste_du <= 0.01 && <span className="bg bg-g">Dossier intégralement réglé</span>}
        </div>

        {/* Transactions — état Stripe RÉEL (§4.4) */}
        <div className="bh" style={{ marginTop: 20, paddingLeft: 0 }}><h2 style={{ fontSize: 15 }}>Paiements &amp; état Stripe</h2></div>
        {!detail ? <div className="empty">Chargement…</div> : detail.transactions.length === 0
          ? <div className="empty" data-testid="argent-no-tx">Aucun paiement enregistré pour l'instant.</div>
          : (
            <table data-testid="table-transactions">
              <thead><tr><th>Moyen</th><th>Type</th><th className="nu">Montant</th><th>État</th><th>Date</th><th>Référence</th></tr></thead>
              <tbody>
                {detail.transactions.map((t, i) => (
                  <tr key={i} data-testid={`tx-${i}`}>
                    <td>{t.mode === 'cb' ? '💳 Carte' : t.mode}</td>
                    <td>{t.type}</td>
                    <td className="nu">{eur(t.montant)}</td>
                    <td><span className={`bg ${(ETAT_TX[t.etat_stripe] || {}).c || 'bg-n'}`} data-testid={`tx-etat-${i}`}>{(ETAT_TX[t.etat_stripe] || {}).l || t.etat_stripe}</span></td>
                    <td className="mono">{dfr(t.date)}</td>
                    <td className="mono" style={{ fontSize: 11, color: 'var(--t3)' }}>{(t.reference || '—').slice(0, 22)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}

        {/* Factures du dossier */}
        <div className="bh" style={{ marginTop: 20, paddingLeft: 0 }}><h2 style={{ fontSize: 15 }}>Factures</h2></div>
        {!detail ? null : detail.factures.length === 0
          ? <div className="empty" data-testid="argent-no-facture">Aucune facture émise. Les factures d'acompte et finale sont générées automatiquement à l'encaissement.</div>
          : (
            <table data-testid="table-factures-dossier">
              <thead><tr><th>Numéro</th><th>Type</th><th className="nu">Montant</th><th>Statut</th><th /></tr></thead>
              <tbody>
                {detail.factures.map((fac) => (
                  <tr key={fac.id} data-testid={`fac-dossier-${fac.id}`}>
                    <td><b className="mono">{fac.numero}</b></td>
                    <td>{fac.libelle_type}</td>
                    <td className="nu">{eur(fac.montant)}</td>
                    <td><span className={`bg ${STATUT_FAC[fac.statut] || 'bg-n'}`}>{fac.libelle_statut}</span></td>
                    <td className="nu"><button className="btn btn-s" data-testid={`fac-dossier-pdf-${fac.id}`} onClick={() => facturesApi.openPdf(fac.id)}>PDF</button></td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
      </div>
    </div>
  );
}

function TabTerrain({ etat, heureRetour, setHeureRetour, onRetour, busy }) {
  const peutRetour = ['livre', 'a_recuperer'].includes(etat.etat);
  return (
    <div className="bk" data-testid="tab-terrain-content">
      <div className="bh"><h2>Le terrain</h2></div>
      <div style={{ padding: 16 }}>
        <div className="nt">Checklist de préparation, photos d'installation et signature de réception : intégrées au lot Documents. Ci-dessous, la saisie de l'heure de retour réelle (le retard éventuel se calcule automatiquement).</div>
        <div style={{ maxWidth: 280, marginTop: 12 }} className="f">
          <label htmlFor="heure-retour">Heure de retour réelle</label>
          <input id="heure-retour" data-testid="input-heure-retour" type="time" value={heureRetour} onChange={(e) => setHeureRetour(e.target.value)} disabled={!peutRetour} />
        </div>
        <button className="btn btn-g" style={{ marginTop: 12 }} data-testid="btn-enregistrer-retour"
          disabled={!peutRetour || busy} onClick={onRetour}>Enregistrer le retour</button>
        {!peutRetour && <div className="lk" style={{ marginTop: 8 }}>🔒 Disponible une fois le matériel livré.</div>}
      </div>
    </div>
  );
}

function TabHisto({ quote, histo }) {
  const evts = (quote.etat_history || []).slice().reverse();
  return (
    <div className="bk" data-testid="tab-histo-content">
      <div className="bh"><h2>L'historique</h2></div>
      <div style={{ padding: 8 }}>
        {evts.length === 0 && (!histo || histo.length === 0) && <div className="empty">Aucun événement pour l'instant.</div>}
        {evts.map((h, i) => (
          <div className="rw" key={`e-${i}`} data-testid={`histo-${i}`}>
            <div className="gw"><div className="t">{h.event} — {h.de} → {h.vers}</div><div className="m">Par {h.acteur}</div></div>
            <div className="am mono" style={{ fontSize: 12, color: 'var(--t3)' }}>{dfr(h.date)}</div>
          </div>
        ))}
        {Array.isArray(histo) && histo.map((h, i) => (
          <div className="rw" key={`h-${i}`}>
            <div className="gw"><div className="t">{h.type || h.action || h.subject || 'Événement'}</div><div className="m">{h.detail || h.message || ''}</div></div>
            <div className="am mono" style={{ fontSize: 12, color: 'var(--t3)' }}>{dfr(h.date || h.created_at)}</div>
          </div>
        ))}
      </div>
    </div>
  );
}
